"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { useUser } from "@/features/auth/hooks/useUser";
import { useAuthModal } from "@/features/auth/hooks/useAuthModal";
import { useUpgradeSuccessModal } from "@/features/auth/hooks/useUpgradeSuccessModal";
import { capture } from "@/lib/analytics/posthog";
import { isPaidPlan } from "@/lib/auth/plans";
import { comingSoonMessage, type BillingInterval, type PaidPlan } from "@/lib/billing/planConfig";
import type { BillingState, PlanChangeKind } from "@/lib/billing/types";
import {
  BillingRequestError,
  createCheckout,
  createPortalLink,
  fetchBillingState,
  resumeSubscription,
} from "../lib/billingApi";
import { comingSoonFor, hasLiveSubscription, type PlanViewer } from "../lib/planCta";
import { savePendingPlanIntent, takePendingPlanIntent } from "../lib/pendingIntent";
import { formatBillingDate } from "../lib/format";

// Both dialogs are fetched on first open: they sit in the root layout, and
// every route's first-load JS would otherwise carry them.
const ChangePlanDialog = dynamic(
  () => import("../components/ChangePlanDialog").then((m) => m.ChangePlanDialog),
  { ssr: false },
);
const CancelSubscriptionDialog = dynamic(
  () => import("../components/CancelSubscriptionDialog").then((m) => m.CancelSubscriptionDialog),
  { ssr: false },
);

export type ActionResult = { ok: true } | { ok: false; message: string };

type Toast = { tone: "success" | "error"; text: string } | null;

type BillingContextValue = {
  /** Live billing state; null until something asked for it (it costs a Creem call). */
  state: BillingState | null;
  loading: boolean;
  error: string | null;
  /** Fetch (or reuse a fresh) billing state. */
  load: (opts?: { force?: boolean }) => Promise<BillingState | null>;
  /** The viewer model plan cards decide their buttons from. */
  viewer: PlanViewer;
  /**
   * The one entry point every "Get Pro" / "Upgrade" button uses: sign in,
   * fresh checkout, or an in-app plan change — whichever this user needs.
   */
  choosePlan: (plan: PaidPlan, interval: BillingInterval, source: string) => Promise<ActionResult>;
  openCancel: (source: string) => void;
  resume: () => Promise<ActionResult>;
  openPortal: () => Promise<ActionResult>;
  /** Plan whose checkout is being created (button spinner). */
  pendingCheckout: PaidPlan | null;
};

const BillingContext = createContext<BillingContextValue | null>(null);

/** How long a loaded state is reused before `load()` asks Creem again. */
const STATE_TTL_MS = 60_000;

export function BillingProvider({ children }: { children: ReactNode }) {
  const { user, plan, subscriptionStatus, planExpiresAt, loading: userLoading, refresh } = useUser();
  const { open: openAuth } = useAuthModal();
  const { open: openUpgradeSuccess } = useUpgradeSuccessModal();
  const router = useRouter();

  // Tagged with the user it belongs to: after a sign-out/sign-in the old
  // person's billing simply reads as null, no reset effect needed.
  const [entry, setEntry] = useState<{ owner: string; state: BillingState } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingCheckout, setPendingCheckout] = useState<PaidPlan | null>(null);
  const [changeTarget, setChangeTarget] = useState<{ plan: PaidPlan; interval: BillingInterval } | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  // Mirrors `entry` so `load` can stay referentially stable per user — an
  // effect that depends on it would otherwise re-fire on every fetch it makes.
  const cacheRef = useRef<{ owner: string; state: BillingState; at: number } | null>(null);
  const inflight = useRef<Promise<BillingState | null> | null>(null);
  const userId = user?.id ?? null;
  const state = entry && entry.owner === userId ? entry.state : null;

  const storeState = useCallback(
    (next: BillingState) => {
      if (!userId) return;
      cacheRef.current = { owner: userId, state: next, at: Date.now() };
      setEntry({ owner: userId, state: next });
    },
    [userId],
  );

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 7000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!userId) return null;
      const cached = cacheRef.current;
      if (!opts?.force && cached && cached.owner === userId && Date.now() - cached.at < STATE_TTL_MS) {
        return cached.state;
      }
      if (inflight.current) return inflight.current;
      setLoading(true);
      const p = fetchBillingState()
        .then((s) => {
          storeState(s);
          setError(null);
          return s;
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : "Couldn't load billing.");
          return null;
        })
        .finally(() => {
          setLoading(false);
          inflight.current = null;
        });
      inflight.current = p;
      return p;
    },
    [userId, storeState],
  );

  /**
   * After any change: this state, the client gates (useUser) and the SERVER-
   * rendered gates all move — e.g. Review Insights decides Audience access on
   * the server, so without the refresh it stayed locked after an upgrade.
   */
  const applyState = useCallback(
    async (next: BillingState) => {
      storeState(next);
      await refresh();
      router.refresh();
    },
    [refresh, storeState, router],
  );

  const viewer: PlanViewer = useMemo(() => {
    const complimentary =
      state?.complimentary ?? (isPaidPlan(plan) && subscriptionStatus === null);
    return {
      plan,
      status: state?.status ?? subscriptionStatus,
      complimentary,
      billed:
        state?.subscription?.plan && state.subscription.interval
          ? { plan: state.subscription.plan, interval: state.subscription.interval }
          : null,
      pending: state?.pendingChange ?? null,
      endsAt: state?.subscription?.currentPeriodEnd ?? planExpiresAt,
    };
  }, [plan, subscriptionStatus, planExpiresAt, state]);

  const choosePlan = useCallback(
    async (target: PaidPlan, interval: BillingInterval, source: string): Promise<ActionResult> => {
      if (userLoading) return { ok: false, message: "One moment — still checking your account." };
      // A plan that isn't on sale: no sign-in detour, checkout or dialog. The
      // server refuses it too; this only saves the round trip.
      if (comingSoonFor(viewer, target)) return { ok: false, message: comingSoonMessage(target) };
      if (plan === "anonymous") {
        savePendingPlanIntent({ plan: target, interval, source });
        openAuth();
        return { ok: true };
      }
      // Signed in per the server hint, but the client session isn't read yet
      // (a few ms after load) — don't send a signed-in user to the sign-in modal.
      if (!userId) return { ok: false, message: "One moment — still checking your account." };
      if (viewer.complimentary) {
        return { ok: false, message: "Your plan is complimentary — there's nothing to buy." };
      }
      if (hasLiveSubscription(viewer)) {
        setChangeTarget({ plan: target, interval });
        return { ok: true };
      }
      setPendingCheckout(target);
      try {
        const url = await createCheckout(target, interval);
        // Fired on the real redirect, not on the click — a refused checkout
        // (409, 429, 503) is not a checkout that started.
        capture("checkout started", { plan: target, interval, source });
        window.location.href = url;
        return { ok: true }; // the button stays busy until the page leaves
      } catch (err) {
        setPendingCheckout(null);
        if (err instanceof BillingRequestError && err.code === "has_subscription") {
          // Our copy of the profile was stale: a subscription exists. Switch
          // to the in-app change instead of failing.
          await refresh();
          setChangeTarget({ plan: target, interval });
          return { ok: true };
        }
        return { ok: false, message: err instanceof Error ? err.message : "Failed to start checkout" };
      }
    },
    [userLoading, plan, userId, viewer, openAuth, refresh],
  );

  const openCancel = useCallback((source: string) => {
    capture("cancel flow opened", { source });
    setCancelOpen(true);
  }, []);

  const resume = useCallback(async (): Promise<ActionResult> => {
    try {
      const { state: next } = await resumeSubscription();
      await applyState(next);
      capture("subscription resumed", { plan: next.plan });
      setToast({
        tone: "success",
        text: next.subscription?.nextChargeAt
          ? `You're all set — your plan renews on ${formatBillingDate(next.subscription.nextChargeAt)}.`
          : "You're all set — your subscription will renew.",
      });
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : "Couldn't resume" };
    }
  }, [applyState]);

  const openPortal = useCallback(async (): Promise<ActionResult> => {
    try {
      const url = await createPortalLink();
      capture("billing portal opened");
      // Same-tab: window.open() after an await is outside the user-activation
      // window, so Safari/iOS block it silently; the link is single-use anyway.
      window.location.href = url;
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : "Failed to open the billing portal" };
    }
  }, []);

  const onPlanChanged = useCallback(
    async (kind: PlanChangeKind, next: BillingState, target: { plan: PaidPlan; interval: BillingInterval }) => {
      // Decide from what was ASKED for (a tier change vs a billing-period
      // switch), not from whether the new plan already reads back — a tier
      // upgrade whose plan write hadn't landed used to be announced as
      // "you're now billed yearly".
      const tierUpgrade = kind === "upgrade" && target.plan !== plan;
      setChangeTarget(null);
      await applyState(next);
      capture("plan changed", { kind, plan: target.plan, interval: target.interval });
      if (tierUpgrade && next.status === "past_due") {
        setToast({
          tone: "error",
          text: "The upgrade payment didn't go through — update your payment method in Account & billing.",
        });
      } else if (tierUpgrade) {
        // The success modal polls until the plan reads upgraded.
        openUpgradeSuccess(target.plan, plan === "pro" || plan === "studio" ? plan : "free");
      } else if (kind === "upgrade") {
        setToast({ tone: "success", text: "Done — you're now billed yearly." });
      } else if (kind === "downgrade") {
        setToast({
          tone: "success",
          text: `Scheduled. You keep your current plan until ${formatBillingDate(next.pendingChange?.effectiveAt)}.`,
        });
      } else if (kind === "undo_downgrade") {
        setToast({ tone: "success", text: "Switch canceled — you stay on your current plan." });
      }
    },
    [applyState, openUpgradeSuccess, plan],
  );

  const onCanceled = useCallback(
    async (next: BillingState) => {
      await applyState(next);
    },
    [applyState],
  );

  // Finish a "Get Pro" that was interrupted by signing in — once per user.
  const intentHandledFor = useRef<string | null>(null);
  useEffect(() => {
    if (userLoading || !userId || plan === "anonymous" || intentHandledFor.current === userId) return;
    intentHandledFor.current = userId;
    const intent = takePendingPlanIntent();
    if (!intent) return;
    if (isPaidPlan(plan) && plan === intent.plan) return; // already has it
    // Off the effect's own tick: choosePlan sets state (dialog, spinner).
    queueMicrotask(() => {
      void choosePlan(intent.plan, intent.interval, `${intent.source}_after_signin`).then((r) => {
        if (!r.ok) setToast({ tone: "error", text: r.message });
      });
    });
  }, [userLoading, userId, plan, choosePlan]);

  const value = useMemo(
    () => ({ state, loading, error, load, viewer, choosePlan, openCancel, resume, openPortal, pendingCheckout }),
    [state, loading, error, load, viewer, choosePlan, openCancel, resume, openPortal, pendingCheckout],
  );

  return (
    <BillingContext.Provider value={value}>
      {children}
      {changeTarget && (
        <ChangePlanDialog
          target={changeTarget}
          onClose={() => setChangeTarget(null)}
          onChanged={onPlanChanged}
          onResume={resume}
          onFixPayment={openPortal}
        />
      )}
      {cancelOpen && (
        <CancelSubscriptionDialog
          onClose={() => setCancelOpen(false)}
          onCanceled={onCanceled}
          onResume={resume}
        />
      )}
      {toast && (
        <div
          role="status"
          className={`fixed bottom-5 left-1/2 z-[110] w-[min(92vw,420px)] -translate-x-1/2 rounded-lg border px-4 py-3 text-sm shadow-2xl ${
            toast.tone === "success"
              ? "border-[#a4d007]/30 bg-[#16202d] text-[#d6e6b0]"
              : "border-red-400/30 bg-[#16202d] text-red-300"
          }`}
        >
          {toast.text}
        </div>
      )}
    </BillingContext.Provider>
  );
}

export function useBilling(): BillingContextValue {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used within BillingProvider");
  return ctx;
}
